import { Model } from 'app/model/backendmodel';
import { Folder } from 'shell-models-lib';
import { AdapterRegistry } from '../adapter-registry';
import { Metamodel } from 'app/model/base/model';
import { WranglingDataflow } from 'app/model/dataflow/wranglingDataflow';
import {
    IDataFlow,
    IDataFlowWranglingTypeProperties,
    IDataFlowProperties,
    IDataFlowSource
} from '../dataflow/IDataflow';
import { Dataset } from 'app/model/dataset/dataset';
import { ReferenceAdapter } from '../reference.adapter';
import { Owned } from 'app/model/base/named';
import { DataFlowParser } from '../dataflow/dataflow.parser';
import { DataFlow } from 'app/model/dataflow/dataflow';
import { NamedAsyncAdapter } from '../named-async.adapter';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { SourceAdapter } from '../dataflow/source.adapter';
import { PowerQuerySource } from 'app/model/dataflow/powerquery-source';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(WranglingDataflow),
    asynchronous: true
})
export class WranglingDataflowAdapter extends NamedAsyncAdapter<WranglingDataflow, IDataFlow> {
    protected async _toResource(wranglingDataflowNamed: WranglingDataflow): Promise<IDataFlow> {
        const wranglingDataflowResource = (await super._toResource(wranglingDataflowNamed)) as IDataFlow;

        const sourceArr: IDataFlowSource[] = [];

        for (const source of wranglingDataflowNamed.sources) {
            sourceArr.push({
                name: source.name,
                script: await this.getDslForSourceDataset(source),
                dataset: this.getDatasetReference(source.sourceDataset)
            });
        }
        const wranglingDataflowProperties: IDataFlowWranglingTypeProperties = {
            sources: sourceArr,
            script: wranglingDataflowNamed.script,
            documentLocale: wranglingDataflowNamed.documentLocale
        };

        wranglingDataflowResource.properties = {
            description: wranglingDataflowNamed.description,
            type: 'WranglingDataFlow',
            folder: this.context.adapterFor(Folder.MODEL).toResource(wranglingDataflowNamed.folder),
            typeProperties: wranglingDataflowProperties
        };
        return wranglingDataflowResource;
    }

    protected async _toModel(
        wranglingDataflowResource: IDataFlow,
        named?: WranglingDataflow
    ): Promise<WranglingDataflow> {
        if (!named) {
            named = WranglingDataflow.MODEL.create<WranglingDataflow>();
        }
        super._toModel(wranglingDataflowResource, named);
        named.description = wranglingDataflowResource.properties.description;

        named.folder = Owned.create<Folder>(Folder.MODEL, named);
        this.context.adapterFor(Folder.MODEL).toModel(wranglingDataflowResource.properties.folder, named.folder);

        const wranglingDataflowProperties: IDataFlowWranglingTypeProperties = (
            wranglingDataflowResource.properties as IDataFlowProperties
        ).typeProperties as IDataFlowWranglingTypeProperties;
        named.script = wranglingDataflowProperties.script;
        named.documentLocale = wranglingDataflowProperties.documentLocale;
        const sourceAdapter = this.context.asyncAdapterFor(Metamodel.modelFor(DataFlowSource));

        if (wranglingDataflowProperties.sources) {
            for (const dataflowSource of wranglingDataflowProperties.sources) {
                const proxy = Owned.create<PowerQuerySource>(Metamodel.modelFor(PowerQuerySource), named);
                proxy.name = dataflowSource.name;
                await sourceAdapter.toModel(dataflowSource, proxy);
                named.sources.add(proxy);
                const dataflowParser = new DataFlowParser();
                const fastQueue = !!this.context.adapterConfig.fastQueue;
                await dataflowParser.parseDataFlowScriptAsync(
                    this.context.contextData,
                    dataflowSource.script,
                    named as unknown as DataFlow,
                    { fastQueue }
                );
            }
        }

        return named;
    }

    private getDatasetReference(dataset: Dataset): Model.DatasetReference {
        return ReferenceAdapter.toResource(dataset, this.context) as Model.DatasetReference;
    }

    private async getDslForSourceDataset(powerQuerySource: PowerQuerySource): Promise<string> {
        const adapter = this.context.asyncAdapterFor(Metamodel.modelFor(DataFlowSource));
        const sourceAdapter = adapter as SourceAdapter;
        const translatedDsl = await sourceAdapter.getDslScript(powerQuerySource as unknown as DataFlowSource);
        return powerQuerySource.outputStream ? `${translatedDsl}` : `${translatedDsl} ${powerQuerySource.name}`;
    }
}
